/**
 * Per-country pricing for the Heidi Remote intro promo. Lifted verbatim from
 * the "Pricing" tables next to each cohort variant in Figma
 * (`Remote-awareness-and-adoption`, node 11164-15005).
 *
 * - `rrp`            — the "Recommended Retail Price (incl. shipping)" column.
 *                      Rendered as the crossed-out price in every cohort bullet.
 * - `discounted`     — the 50% off price (incl. shipping). Used by the monthly
 *                      and yearly cohorts.
 * - `introPrice`     — the introductory promo price (incl. shipping). Used by
 *                      the free / trialling cohort only.
 * - `yearlySavings`  — savings vs monthly billing. Shown only on the monthly
 *                      cohort modal, only for Clinician-tier users.
 *
 * Country keys are ISO 3166-1 alpha-2 and intentionally narrow to the five
 * markets the promo ships to. `getRemotePromoPricing(country)` returns
 * `undefined` for any country outside this set — the eligibility hook will
 * have already gated on country, so consumers should expect a value, but the
 * narrow union keeps the modal defensive against bad inputs.
 */

export type RemotePromoCountry = 'AU' | 'CA' | 'NZ' | 'GB' | 'US';

export type RemotePromoPricing = {
  /** Crossed-out RRP shown alongside the discounted / intro price. */
  rrp: string;
  /** 50% off price — used by monthly + yearly cohorts. */
  discounted: string;
  /** Introductory promo price — used by the free / trialling cohort. */
  introPrice: string;
  /** Annual-vs-monthly savings; only relevant for Clinician-tier monthly users. */
  yearlySavings: string;
};

export const REMOTE_PROMO_PRICING_TABLE: Record<RemotePromoCountry, RemotePromoPricing> =
  {
    AU: {
      rrp: 'A$399.99',
      discounted: 'A$199.99',
      introPrice: 'A$299.99',
      yearlySavings: 'A$420',
    },
    US: {
      rrp: '$299.99',
      discounted: '$149.99',
      introPrice: '$224.99',
      yearlySavings: '$480',
    },
    CA: {
      rrp: 'C$379.99',
      discounted: 'C$189.99',
      introPrice: 'C$284.99',
      yearlySavings: 'C$360',
    },
    GB: {
      rrp: '£209.99',
      discounted: '£104.99',
      introPrice: '£157.49',
      yearlySavings: '£180',
    },
    NZ: {
      rrp: 'NZ$459.99',
      discounted: 'NZ$229.99',
      introPrice: 'NZ$344.99',
      yearlySavings: 'NZ$420',
    },
  };

const SUPPORTED_COUNTRIES = new Set<RemotePromoCountry>(['AU', 'US', 'CA', 'GB', 'NZ']);

/**
 * Heidi's user.country field stores **full country names** (e.g. "Australia",
 * "United States") rather than ISO 3166-1 alpha-2 codes — see
 * `src/lists/countriesToRegion.ts` and `useIsAustralianGP`. Map the names we
 * ship to onto our internal ISO key so the pricing table + URL builder can
 * stay small and locale-segment friendly.
 */
const FULL_NAME_TO_ISO: Record<string, RemotePromoCountry> = {
  Australia: 'AU',
  Canada: 'CA',
  'New Zealand': 'NZ',
  'United Kingdom': 'GB',
  'United States': 'US',
};

export function isRemotePromoCountry(value: unknown): value is RemotePromoCountry {
  return (
    typeof value === 'string' && SUPPORTED_COUNTRIES.has(value as RemotePromoCountry)
  );
}

/**
 * Resolves whatever Heidi has stored on `user.country` to one of our five
 * supported ISO codes. Accepts:
 *
 * - Full names ("Australia", "United States") — the canonical Heidi format.
 * - Two-letter ISO codes ("AU", "US") — defensive in case the data shape changes.
 *
 * Returns `undefined` for anything else (other countries, lowercase, null, …).
 */
export function normalizeRemotePromoCountry(
  country: string | null | undefined
): RemotePromoCountry | undefined {
  if (!country) return undefined;
  if (country in FULL_NAME_TO_ISO) return FULL_NAME_TO_ISO[country];
  if (isRemotePromoCountry(country)) return country;
  return undefined;
}

export function getRemotePromoPricing(
  country: string | null | undefined
): RemotePromoPricing | undefined {
  const iso = normalizeRemotePromoCountry(country);
  if (!iso) return undefined;
  return REMOTE_PROMO_PRICING_TABLE[iso];
}
